import React, { useState } from 'react';
import { Card, Container } from 'react-bootstrap';
import CreateTestCycleForm from '../CreateTestCycleForm';
import { formatDate } from '../../../../lib/utils';
import { updateTestCycle } from '../../../../modules/testcycles';
import { useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { Alert, Button } from '@mui/material';
import { checkRequiredFieldsInCycle } from '../../../../lib/executionUtils';
import useExecutionScope from '../../../../hooks/useExecutionScope';
import { isExecutiveManager } from '../../../../lib/userUtils';
import { useInvalidateQuery, useJenkinsJobs, useProjectUsers, useTestCycleConfig } from '../../../../query/apiQueries';
import ConfirmationModal from '../../../elements/ConfirmationModal';

const TestCycleDetails = ({ data, fetchTestCycleData }) => {
  const defaultFormData = {
    name: data?.name,
    description: data?.description || '',
    startDate: data && data?.start_date ? formatDate(data.start_date) : '',
    endDate: data && data?.end_date ? formatDate(data.end_date) : '',
    status: data?.status?.id,
    type: data?.type_details?.id || '',
    owner: data?.owner || '',
    job: data?.job || '',
    releaseVersion: data?.release_version || '',
    purpose: data?.purpose_details?.id || '',
    geographies: data?.geographies || [],
    languages: data?.languages || [],
    cloudDevice: data?.cloud_device || [],
    platforms: data?.platforms || [],
    subPlatforms: data?.sub_platforms || [],
    browsers: data?.browsers || [],
  }
  const [formData, setFormData] = useState(defaultFormData);
  const { addNotification } = useNotification();
  const [isEditing, setIsEditing] = useState(false);
  const [isFormEdited, setIsFormEdited] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ show: false, type: null, message: '' });
  const [error, setError] = useState(null);
  const { projectId } = useParams();
  const { isScopeDefined, isPlatformsDefined } = useExecutionScope(projectId);
  const invalidateQueries = useInvalidateQuery();

  const isCycleCompleted = data && data?.completion_date
  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData(defaultFormData)
    setError(null);
  }

  const handleSaveClick = async (force = false) => {
  try {
    if (isPlatformsDefined) {
      const errorMessage = checkRequiredFieldsInCycle(formData);
      if (errorMessage) {
        setError(errorMessage);
        return;
      }
    }
    const testcycle = {
        ...formData,
        force
      };
    const response = await updateTestCycle(data.id, { testcycle });
    if (response?.is_duplicate) {
      setError(null);
      setConfirmModal({
        show: true,
        type: 'duplicate',
        message: (
          <>
            {response.message}
            <br />
            <strong>Do you want to update it anyway?</strong>
          </>
        ),
      });
      return;
    }
    if (response && response?.success === false) {
      setError(response?.message);
      return;
    }
    addNotification('success', 'Test cycle updated successfully!');
      invalidateQueries([
        { key: 'testcycles', type: 'listWithParams' },
      ]);

    fetchTestCycleData();
    setIsEditing(false);
    setIsFormEdited(false);
    setError(null);
  } catch (error) {
      const errorMessage =
        error?.message || 'Failed to update test case. Please try again later.';
      addNotification('danger', errorMessage);
  }
};

  const { data: projectUsers = [], isLoading: loadingUsers } = useProjectUsers(projectId);
  const { data: config = [], isLoading: loadingConfig } = useTestCycleConfig(projectId);
  const { data: jobs = [] } = useJenkinsJobs(projectId);

  return (
    <>
    <Container fluid className="px-0">
      <Card className="shadow-sm mb-4">
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ height: '50px' }}>
          <h5 className="mb-0">Test Cycle Details</h5>
          {isEditing ? (
            <div>
              <Button
                variant="contained"
                onClick={() => handleSaveClick()}
                disabled={formData.name === '' || !isFormEdited}>
                Save
              </Button>
              <Button
                sx={{ marginLeft: '.5rem' }}
                variant="secondary"
                onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          ) : (
            !isExecutiveManager() &&
            <Button
              variant="contained"
              onClick={handleEditClick}
              title={
                data?.completion_date === null
                  ? 'Edit'
                  : 'Test cycle is already completed'
              }
            >
              Edit
            </Button>
          )}
        </Card.Header>
        <Card.Body>
          {error && (
            <Alert severity="error" className="mb-3">
              {error}
            </Alert>
          )}
          <CreateTestCycleForm
            formData={formData}
            isEditing={isEditing}
            isCycleCompleted={isCycleCompleted}
            setFormData={setFormData}
            mode="edit"
            setIsFormEdited={setIsFormEdited}
            statuses={config?.status}
            purposeOptions={config?.purpose}
            typeOptions={config?.type}
            jobs={jobs}
            projectUsers={projectUsers || loadingUsers}
            isLoading={loadingConfig || loadingUsers}
            isScopeDefined={isScopeDefined}
          />
        </Card.Body>
      </Card>
    </Container>
    <ConfirmationModal
      show={confirmModal.show}
      handleClose={() => setConfirmModal({ show: false })}
      handleConfirm={() => { setConfirmModal({ show: false }); handleSaveClick(true); }}
      title="Duplicate Test Cycle"
      body={confirmModal.message}
      buttonLabel="Continue Anyway"
      buttonLabelSecondary="Cancel"
      buttonStyle="primary"
    />
</>
  );
};

export default TestCycleDetails;
